<template>
  <div v-if="isFavouriteFeatureEnabled">
    <button
      v-if="isFavourite"
      :class="[$style.favouriteButton, isFavouritesButtonDisabled && $style.loading]"
      :disabled="isFavouritesButtonDisabled"
      data-test="favourite-button"
      @click.prevent.stop="removeFavourite"
    >
      <HgIcon :icon="isFavouritesButtonDisabled ? 'SpinnerHalf' : 'Heart'" theme="current" />
      {{ t('favourites.remove') }}
    </button>

    <button
      v-else
      :class="[$style.favouriteButton, isFavouritesButtonDisabled ? $style.loading : '']"
      :disabled="isFavouritesButtonDisabled"
      data-test="favourite-button"
      @click.prevent.stop="saveFavourite"
    >
      <HgIcon :icon="isFavouritesButtonDisabled ? 'SpinnerHalf' : 'HeartOutline'" theme="current" />
      {{ t('favourites.save') }}
    </button>
  </div>
  <div v-else :class="$style.loadingPlaceholder" />
</template>

<script lang="ts" setup>
import { HgIcon } from '@smg-real-estate/domus';
import { computed } from 'vue';

import { InteractionContainerName } from '@/helpers/GTM/GTMParameters';
import { useFavouritesStore } from '@/store/modules/favourites';
import { useListingStore } from '@/store/modules/listing';
import { trackFavouriteRemove } from '@/trackers/Favourite/Favourite.tracking';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const listingStore = useListingStore();
const favouritesStore = useFavouritesStore();

defineOptions({
  name: 'FavouriteButton',
});

interface Props {
  /**
   * The listing ID
   */
  listingId: string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  redirectToLoginPage: [];
}>();

const isFavouriteFeatureEnabled = computed((): boolean => {
  return favouritesStore.isFavouritesEnabled;
});
const isFavourite = computed((): boolean => {
  return favouritesStore.isListingFavourite(props.listingId);
});
const isFavouritesButtonDisabled = computed((): boolean => {
  return favouritesStore.isFavouritesButtonDisabled;
});

function saveFavourite() {
  favouritesStore.saveFavourite({
    listingId: props.listingId,
    pageInteractionName: InteractionContainerName.PDP,
    listingItem: listingStore.listing,
  });
  emit('redirectToLoginPage');
}
function removeFavourite() {
  favouritesStore.removeFavourite(props.listingId);
  trackFavouriteRemove(InteractionContainerName.PDP, props.listingId);
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

.favouriteButton {
  display: flex;
  align-items: center;
  overflow: hidden;
  color: var(--base-color-link);
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  svg {
    margin-right: $space-25;
  }

  &.loading {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.loadingPlaceholder {
  height: 18px;
}

@media (min-width: $viewport-md-min) {
  .controlBarButton {
    display: flex;

    svg {
      margin-right: $space-50;
    }
  }
}
</style>
